import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import {
  Users, CheckCircle, XCircle, DollarSign, AlertCircle,
  Video, RefreshCw, TrendingUp, Clock, Wallet,
  FileText, Building2, BarChart2, Calendar
} from 'lucide-react';

const T = {
  bg:'#f8f7ff', surface:'#ffffff', border:'#e8e4f3', borderL:'#f5f3ff',
  purple:'#7c3aed', purpleBg:'#ede9fe', purpleText:'#6d28d9',
  text:'#1a1035', textMd:'#4a3f6b', textSm:'#8b7fb0', textXs:'#b8b3cc',
  green:'#059669', greenBg:'#d1fae5',
  red:'#dc2626', redBg:'#fee2e2',
  yellow:'#d97706', yellowBg:'#fef3c7',
  orange:'#ea580c', orangeBg:'#ffedd5',
  blue:'#2563eb', blueBg:'#dbeafe',
  font:"'Poppins', sans-serif",
  shadow:'0 1px 4px rgba(124,58,237,0.06)',
  shadowMd:'0 4px 20px rgba(124,58,237,0.1)',
};

const fmtINR = n => `₹${Number(n||0).toLocaleString('en-IN',{minimumFractionDigits:2})}`;
const fmt = n => String(n).padStart(2,'0');

function BigCard({ icon: Icon, label, value, sub, color, bg, large }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px', boxShadow: T.shadow, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={20} color={color}/>
      </div>
      <div style={{ fontWeight: 800, fontSize: large ? 28 : 24, color: T.text, lineHeight: 1 }}>{value}</div>
      <div style={{ fontWeight: 600, fontSize: 13, color: T.textMd }}>{label}</div>
      {sub && <div style={{ fontWeight: 400, fontSize: 11, color: T.textXs }}>{sub}</div>}
    </div>
  );
}

function SectionHeader({ title, sub }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontWeight: 700, fontSize: 16, color: T.text }}>{title}</div>
      {sub && <div style={{ fontWeight: 400, fontSize: 12, color: T.textSm, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const load = async () => {
    setLoading(true);
    try {
const [attRes, clientRes, taskRes] = await Promise.all([
  api.get('/attendance/admin/today').catch(() => ({ data: { counts: {}, overview: [] } })),
  api.get('/clients/reports/overview').catch((e) => {
    console.error('Finance overview failed:', e?.response?.status, e?.response?.data);
    return { data: {} };
  }),
  api.get('/tasks/reports/overview').catch((e) => {
    console.error('Tasks overview failed:', e?.response?.status, e?.response?.data);
    return { data: {} };
  }),
]);
      setData({
        attendance: attRes.data,
        clients: clientRes.data,
        tasks: taskRes.data,
      });
      setLastRefresh(new Date());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const timeStr = `${fmt(now.getHours())}:${fmt(now.getMinutes())}`;

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
      <div style={{ width: 32, height: 32, border: '3px solid #ede9fe', borderTop: `3px solid ${T.purple}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}/>
    </div>
  );

  const att = data?.attendance || {};
  const cl = data?.clients || {};
  const tk = data?.tasks || {};

  const attCounts = att.counts || {};
  const totalStaff = (att.overview || []).length;
  const presentCount = attCounts.present || 0;
  const absentCount = attCounts.absent || 0;

  const completedVideos = (tk.by_status?.client_approved || 0) + (tk.by_status?.uploaded || 0);
  const pendingVideos = (tk.total || 0) - completedVideos;
  const reworkVideos = (tk.by_status?.rework || 0) + (tk.by_status?.client_rework || 0);
  const pendingApprovals = tk.by_status?.client_approval || 0;

  return (
    <div style={{ fontFamily: T.font }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontWeight: 800, fontSize: 26, color: T.text, letterSpacing: -0.5, marginBottom: 4 }}>Dashboard</h1>
          <p style={{ fontWeight: 400, fontSize: 14, color: T.textSm }}>{dateStr}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 12, color: T.textXs }}>Last refresh: {lastRefresh.toLocaleTimeString('en-IN')}</div>
          <button onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 9, padding: '7px 13px', fontSize: 12, fontFamily: T.font, color: T.textSm, cursor: 'pointer' }}>
            <RefreshCw size={13}/>Refresh
          </button>
        </div>
      </div>

      {/* ── SECTION 1: TODAY SNAPSHOT ─────────────────── */}
      <SectionHeader title="Today at a Glance" sub="Live numbers — refresh to update"/>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 28 }}>
        <BigCard icon={Users}       label="Present Today"      value={presentCount}         color={T.green}  bg={T.greenBg}/>
        <BigCard icon={XCircle}     label="Absent Today"       value={absentCount}          color={T.red}    bg={T.redBg}/>
        <BigCard icon={CheckCircle} label="Videos Completed"   value={completedVideos}      color={T.purple} bg={T.purpleBg}/>
        <BigCard icon={Video}       label="Videos Pending"     value={pendingVideos}        color={T.yellow} bg={T.yellowBg}/>
        <BigCard icon={AlertCircle} label="Rework"             value={reworkVideos}         color={T.red}    bg={T.redBg}  sub="Need re-editing"/>
        <BigCard icon={Clock}       label="Pending Approval"   value={pendingApprovals}     color={T.blue}   bg={T.blueBg} sub="Awaiting client"/>
        <BigCard icon={AlertCircle} label="Overdue Tasks"      value={tk.overdue || 0}      color={T.orange} bg={T.orangeBg}/>
        <BigCard icon={Building2}   label="Active Clients"     value={cl.total_clients || 0} color={T.purple} bg={T.purpleBg}/>
      </div>

      {/* ── SECTION 2: FINANCE SUMMARY ───────────────── */}
      <SectionHeader title="Finance Overview" sub="Client billing and collections"/>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 28 }}>
        <BigCard icon={TrendingUp}  label="Total Billed"       value={fmtINR(cl.total_billed)}       color={T.blue}   bg={T.blueBg}/>
        <BigCard icon={Wallet}      label="Total Collected"    value={fmtINR(cl.total_paid)}         color={T.green}  bg={T.greenBg}/>
        <BigCard icon={DollarSign}  label="Outstanding"        value={fmtINR(cl.total_outstanding)}  color={cl.total_outstanding > 0 ? T.red : T.green} bg={cl.total_outstanding > 0 ? T.redBg : T.greenBg} sub={cl.overdue_count > 0 ? `${cl.overdue_count} overdue clients` : 'All clear'}/>
      </div>

      {/* ── SECTION 3: TODAY STAFF LIST ──────────────── */}
      {(att.overview || []).length > 0 && (
        <>
          <SectionHeader title="Staff Status Today" sub="Login and logout times"/>
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden', boxShadow: T.shadow, marginBottom: 28 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 90px', padding: '10px 18px', background: '#faf9ff', borderBottom: `1px solid ${T.borderL}` }}>
              {['Employee', 'Login', 'Logout', 'Status'].map(h => (
                <span key={h} style={{ fontWeight: 600, fontSize: 11, color: T.textSm, textTransform: 'uppercase', letterSpacing: 0.7 }}>{h}</span>
              ))}
            </div>
            {(att.overview || []).map((emp, idx) => {
              const s = emp.attendance?.status || 'absent';
              const sCfg = {
                present:    { bg: T.greenBg,  c: T.green,  l: 'Present' },
                absent:     { bg: T.redBg,    c: T.red,    l: 'Absent' },
                leave:      { bg: T.purpleBg, c: T.purple, l: 'On Leave' },
                permission: { bg: '#fce7f3',  c: '#db2777',l: 'Permission' },
                holiday:    { bg: T.yellowBg, c: T.yellow, l: 'Holiday' },
                half_day:   { bg: '#ccfbf1',  c: '#0d9488',l: 'Half Day' },
              }[s] || { bg: '#f3f4f6', c: '#6b7280', l: s };
              const fmtT = iso => {
                if (!iso) return '—';
                const d = new Date(iso), h = d.getHours(), m = d.getMinutes();
                return `${fmt(h > 12 ? h - 12 : h || 12)}:${fmt(m)} ${h >= 12 ? 'PM' : 'AM'}`;
              };
              return (
                <div key={emp.id} style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 90px', padding: '11px 18px', borderBottom: idx < att.overview.length - 1 ? `1px solid ${T.borderL}` : 'none', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: T.purpleBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, color: T.purpleText, flexShrink: 0 }}>
                      {emp.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: T.text }}>{emp.name}</div>
                      <div style={{ fontSize: 11, color: T.textXs, textTransform: 'capitalize' }}>{emp.role}</div>
                    </div>
                  </div>
                  <span style={{ fontSize: 13, color: T.textMd }}>{fmtT(emp.attendance?.login_time)}</span>
                  <span style={{ fontSize: 13, color: T.textMd }}>{fmtT(emp.attendance?.logout_time)}</span>
                  <span style={{ background: sCfg.bg, color: sCfg.c, fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20 }}>{sCfg.l}</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── SECTION 4: TASK STATUS BREAKDOWN ────────── */}
      {tk.total > 0 && (
        <>
          <SectionHeader title="Project Tasks Overview" sub="All tasks across all clients"/>
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', boxShadow: T.shadow, marginBottom: 28 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                { l: 'Ready to Edit', v: tk.by_status?.ready_to_edit || 0, c: T.green, bg: T.greenBg },
                { l: 'In Review', v: tk.by_status?.review || 0, c: T.purple, bg: T.purpleBg },
                { l: 'Rework', v: (tk.by_status?.rework || 0) + (tk.by_status?.client_rework || 0), c: T.red, bg: T.redBg },
                { l: 'Client Approval', v: tk.by_status?.client_approval || 0, c: T.blue, bg: T.blueBg },
                { l: 'Approved', v: tk.by_status?.client_approved || 0, c: T.green, bg: T.greenBg },
                { l: 'Uploaded', v: tk.by_status?.uploaded || 0, c: '#16a34a', bg: '#f0fdf4' },
              ].map(({ l, v, c, bg }) => (
                <div key={l} style={{ background: bg, borderRadius: 10, padding: '10px 14px', minWidth: 110, textAlign: 'center' }}>
                  <div style={{ fontWeight: 800, fontSize: 20, color: c }}>{v}</div>
                  <div style={{ fontWeight: 500, fontSize: 11, color: c, marginTop: 3, opacity: 0.8 }}>{l}</div>
                </div>
              ))}
            </div>

            {/* Progress bar */}
            {tk.total > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12, color: T.textSm }}>
                  <span>Overall Completion</span>
                  <span style={{ fontWeight: 700, color: T.green }}>{Math.round((completedVideos / tk.total) * 100)}%</span>
                </div>
                <div style={{ height: 8, background: '#f0edfb', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.round((completedVideos / tk.total) * 100)}%`, background: `linear-gradient(90deg, ${T.green}, #34d399)`, borderRadius: 10, transition: 'width 0.8s' }}/>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── SECTION 5: EDITOR WORKLOAD ───────────────── */}
      {(tk.editor_workload || []).length > 0 && (
        <>
          <SectionHeader title="Editor Workload" sub="Tasks per editor — completed excludes rework"/>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginBottom: 28 }}>
            {(tk.editor_workload || []).map(ew => (
              <div key={ew.editor.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden', boxShadow: T.shadow }}>
                <div style={{ background: '#1a1035', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: '#fff', flexShrink: 0 }}>
                    {ew.editor.name[0].toUpperCase()}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#fff' }}>{ew.editor.name}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '12px 16px', gap: 8 }}>
                  {[
                    { l: 'Total', v: ew.total, c: T.text },
                    { l: 'Done', v: ew.completed, c: T.green },
                    { l: 'Rework', v: ew.rework, c: T.red },
                  ].map(({ l, v, c }) => (
                    <div key={l} style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 800, fontSize: 18, color: c }}>{v}</div>
                      <div style={{ fontSize: 10, color: T.textXs, marginTop: 2 }}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── SECTION 6: TOP CLIENTS ───────────────────── */}
      {(cl.top_clients || []).length > 0 && (
        <>
          <SectionHeader title="Top Clients by Payment" sub="Highest paying clients"/>
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden', boxShadow: T.shadow }}>
            {(cl.top_clients || []).map((c, idx) => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderBottom: idx < cl.top_clients.length - 1 ? `1px solid ${T.borderL}` : 'none' }}>
                <div style={{ width: 26, height: 26, background: T.purpleBg, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, color: T.purpleText, flexShrink: 0 }}>
                  {idx + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: T.text }}>{c.company_name}</div>
                  <div style={{ fontSize: 11, color: T.textXs }}>{c.client_code}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: T.green }}>{fmtINR(c.total_paid)}</div>
                  {c.total_outstanding > 0 && <div style={{ fontSize: 11, color: T.red }}>{fmtINR(c.total_outstanding)} pending</div>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}